import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Typography from '@mui/material/Typography';
import { useHistory, useLocation } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';

import AuthHeader from '@/common/components/AuthHeader/AuthHeader';
import { routes } from '@/app/routing';
import { useAuth } from '@/app/providers/AuthProvider';
import { Notification } from '@/common/utils';
import {
  useCompleteNewPasswordMutation,
  useLoginMutation,
  useUpdateCurrentUserMutation,
} from '@/entities/user';
import loginStyles from '@/modules/login/login.styles';
import SetupAccountForm from '@/modules/login/SetupAccountForm';
import CopyrightSup from '@/common/components/CopyrightSup';
import { ROLES } from '@/common/constants/constants';
import { getInviteCredentials } from '@/app/routing/invite-params';

const SetupAccountModule = () => {
  const { t } = useTranslation();
  const copyrightSup = <CopyrightSup />;
  const history = useHistory();
  const location = useLocation();
  const { refreshUser } = useAuth();
  const loginMutation = useLoginMutation();
  const completeNewPasswordMutation = useCompleteNewPasswordMutation();
  const updateCurrentUserMutation = useUpdateCurrentUserMutation();
  const autoLoginAttemptedRef = useRef(false);
  const [user, setUser] = useState(null);
  const [isAccountReady, setIsAccountReady] = useState(false);
  const [inviteLinkExpired, setInviteLinkExpired] = useState(false);
  const [isAutoSigningIn, setIsAutoSigningIn] = useState(false);
  const { inviteEmail, invitePassword, inviteFirstName, inviteLastName } =
    useMemo(
      () => getInviteCredentials(location.search, location.hash),
      [location.hash, location.search]
    );

  const hasInviteCredentials = !!inviteEmail && !!invitePassword;
  const isInviteLinkCheckPending =
    hasInviteCredentials &&
    !autoLoginAttemptedRef.current &&
    !user &&
    !inviteLinkExpired &&
    !isAccountReady;
  const loadCurrentUser = useCallback(async () => {
    const nextUser = await refreshUser();
    return nextUser;
  }, [refreshUser]);

  const goToDefaultRoute = useCallback(async () => {
    const nextUser = await loadCurrentUser();
    history.push(
      nextUser?.role === ROLES.USER
        ? routes.decedentInitiation
        : routes.adminPanel.decedents
    );
  }, [history, loadCurrentUser]);

  const returnToLogin = useCallback(() => {
    history.replace(routes.login);
  }, [history]);

  useEffect(() => {
    if (!hasInviteCredentials) {
      returnToLogin();
      return;
    }

    if (autoLoginAttemptedRef.current) {
      return;
    }

    autoLoginAttemptedRef.current = true;
    setInviteLinkExpired(false);
    setIsAutoSigningIn(true);

    const runAutoLogin = async () => {
      try {
        const auth = await loginMutation.mutateAsync({
          email: inviteEmail,
          password: invitePassword,
        });

        if (
          auth?.nextStep?.signInStep ===
          'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
        ) {
          setUser(auth);
          setIsAccountReady(false);
          return;
        }

        await goToDefaultRoute();
      } catch (_error) {
        setUser(null);
        setInviteLinkExpired(true);
      } finally {
        setIsAutoSigningIn(false);
      }
    };

    void runAutoLogin();
  }, [
    goToDefaultRoute,
    hasInviteCredentials,
    inviteEmail,
    invitePassword,
    loginMutation,
    returnToLogin,
  ]);

  const onSetPasswordSubmit = useCallback(
    async ({ password, firstName, lastName }) => {
      try {
        setInviteLinkExpired(false);
        await completeNewPasswordMutation.mutateAsync({
          password,
        });
        await updateCurrentUserMutation.mutateAsync({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
        });
        await loadCurrentUser();
        setUser(null);
        setIsAccountReady(true);
      } catch (error) {
        Notification.error(error?.message);
        throw error;
      }
    },
    [completeNewPasswordMutation, loadCurrentUser, updateCurrentUserMutation]
  );

  if (isAccountReady) {
    return (
      <Box component="section" sx={loginStyles.readyPage}>
        <AuthHeader withLogout />

        <Box sx={loginStyles.readyContent}>
          <Box sx={loginStyles.readyIconWrap}>
            <CheckCircleOutlineIcon sx={loginStyles.readyIcon} />
          </Box>
          <Typography variant="h2" textAlign="center" maxWidth="440px">
            {t('Your account is ready')}
          </Typography>
          <Typography
            variant="body1"
            textAlign="center"
            color="text.secondary"
            sx={loginStyles.readyDescription}
          >
            {t('Your password has been successfully updated.\n')}
            <Trans
              i18nKey="You are now logged in and can start using PAEM After Care<copyrightSup/>."
              components={{ copyrightSup }}
            />
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={goToDefaultRoute}
            sx={loginStyles.readyButton}
          >
            {t('Get started')}
          </Button>
        </Box>
      </Box>
    );
  }

  if (inviteLinkExpired) {
    return (
      <Box component="section" sx={loginStyles.readyPage}>
        <AuthHeader withLogout />

        <Box sx={loginStyles.readyContent}>
          <Box sx={loginStyles.expiredIconWrap}>
            <ErrorOutlineIcon sx={{ fontSize: 36, color: 'system.red' }} />
          </Box>
          <Typography variant="h2" textAlign="center" maxWidth="440px">
            {t('This sign-in link does not work anymore')}
          </Typography>
          <Typography
            variant="body1"
            textAlign="center"
            color="text.secondary"
            sx={loginStyles.readyDescription}
          >
            {t(
              'This registration link is no longer valid.\nPlease request a new registration link to continue.'
            )}
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={returnToLogin}
            sx={loginStyles.readyButton}
          >
            {t('Back to login')}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box component="section" sx={loginStyles.container}>
      <AuthHeader withLogout />

      <Box sx={loginStyles.formWrapper}>
        <Typography variant="h2" textAlign="center" mb={10}>
          {t('Set up your account')}
        </Typography>

        {isAutoSigningIn || isInviteLinkCheckPending ? (
          <Typography textAlign="center" sx={loginStyles.subTitle}>
            {t('Checking your access link...')}
          </Typography>
        ) : user ? (
          <SetupAccountForm
            email={
              user?.challengeParam?.userAttributes?.email ||
              user?.attributes?.email ||
              user?.username ||
              inviteEmail
            }
            firstName={inviteFirstName}
            lastName={inviteLastName}
            onSubmit={onSetPasswordSubmit}
            isPending={
              completeNewPasswordMutation.isPending ||
              updateCurrentUserMutation.isPending
            }
          />
        ) : (
          <Button
            variant="contained"
            size="large"
            onClick={returnToLogin}
            sx={{ mt: 2 }}
          >
            {t('Back to login')}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default SetupAccountModule;
